// Shared state singleton + the small primitives every module leans on: DOM
// lookup, escaping, formatting, fetch helpers, and the KPI delta badge. Kept in
// the same vanilla style as the rest of the client so the extraction stays a
// structural move, not a rewrite.

// The client is a small SPA with a single shared mutable state object. API
// payloads stay `any` (the server owns those shapes); the control state that the
// UI reassigns is typed so the wider value ranges (e.g. days: number | 'all')
// are explicit and property typos are caught.
export interface SessionFilters {
  facets: Record<string, string>
  q: string
  artifact: string
  artifactKind: string
  // Exact-artifact drill-in (a "Sessions →" button), which replaces the substring
  // `artifact` search rather than refining it — `artifact` then holds only the
  // display label. `artifactSubtree` widens it to descendants (an epic/story row).
  artifactId: string
  artifactSubtree: boolean
  outcomes: string[]
  // Resolved window for the request (ISO); '' when the window is 'all'.
  from: string
  to: string
  // Sessions-tab team filter (a team id, '' = all teams). A narrowing filter on
  // the org-wide list — NOT the dashboard's top-bar scope; the Sessions list stays
  // all-teams by default so cross-team discovery (title-only + request access) works.
  team: string
  // Sessions-tab user filter (a user id, 'me', or '' = everyone). Same
  // discovery-not-access deal as `team`: narrows whose rows show, never what
  // opens. 'me' is sent as-is; the server resolves it to the viewer.
  user: string
}

// Sessions-list time window. `preset` drives from/to; 'custom' reads the date
// inputs; 'all' means no bound. Independent of the dashboard KPI window.
export interface SessTime {
  preset: 7 | 14 | 30 | 90 | 'all' | 'custom'
  from: string // ISO date (yyyy-mm-dd) for custom range
  to: string
}

export interface ClientState {
  // The top-level tab the app is showing. Mirrored into the URL hash by the
  // router; setView() keeps it in step with the DOM.
  view: 'dashboard' | 'artifacts' | 'sessions' | 'settings' | 'benchmarks' | 'taskreplay' | 'replay' | 'skills' | 'tools' | 'insights' | 'models'
  // The session whose detail drawer is open (null = drawer closed). Mirrored into
  // the URL as `?session=<id>` so a session is shareable / reload-survivable.
  open: string | null
  artKind: string
  me: any // the authenticated user ({ id, email, name, role, teamId }); set at boot
  overview: any
  home: any // Explore (question-led) stats; null until fetched
  asked: any // the question the user clicked through from, for the grounding banner (null = none)
  filters: Partial<SessionFilters> // starts {}, filled in by applyFilters()
  facets: any[]
  dist: Record<string, any[]>
  measures: any[]
  metric: string | null // which headline KPI's detail view is open (null = overview)
  outcomeTypes: any[]
  days: number | 'all' // top-level KPI window (drives the whole headline row + cost-artifact curves)
  // Top-level team/repo scope (AL-100), a sibling of `days`: '' = unset (global on
  // that axis). Threaded into every metric request via scopeQs(). filterOpts is the
  // /api/filters payload backing the selectors (null until fetched).
  // `user`: the dashboard user scope ('' = none; 'me' or a user id). Only
  // rendered for roles the server would honor (canPickUser); the server clamps
  // regardless.
  scope: { team: string; repos: string[]; user: string }
  // Highlights reads the SAME state.scope (so its "See the data →" drill-in can't
  // contradict the chart it lands on), but it's a different tab — a scope change
  // made on the Dashboard sets this instead of refetching a digest nobody is
  // looking at. renderHighlights() consumes + clears it on tab-in. See home.ts.
  insightsSub: 'highlights' | 'recommendations'
  hlStale: boolean
  filterOpts: { role: string; teams: Array<{ id: string; name: string }>; allTeams: Array<{ id: string; name: string }>; repos: Array<{ id: string; label: string }>; defaultTeamId: string | null; canPickUser: boolean; users: Array<{ id: string; name: string | null; email: string }> } | null
  // The org directory (/api/users) backing the Sessions user filter and the
  // Share picker; null until first fetched (loadOrgUsers), then cached for the
  // page's lifetime — the roster changes rarely enough that stale is fine.
  orgUsers: Array<{ id: string; name: string | null; email: string; teamName: string | null }> | null
  sr: { outcomes: string[]; bucket: string; by: string; filters: Record<string, string[]> } // success-rate detail controls
  // cost-per-artifact detail controls. `kind` follows defaultKind until the user
  // toggles it (userPicked), after which it sticks and the headline tile mirrors
  // it. `kindResolved` marks that defaultKind has been applied — it is a LANDING
  // default, applied once per page, so a later repaint (every window or scope change
  // recomputes it) can't move the toggle out from under someone reading it. See
  // caKind.ts. `bucket` is the curve granularity: '' = auto-derived from the window;
  // a manual pick overrides until the window changes.
  // `kind` (feature|pr) scopes the whole section; `complexity` scopes the cost-by-artifact view.
  // `by` is the converted-vs-unconverted chart's axis: '' = time (the bucket control),
  // or 'type' | 'epic' | 'item' for the non-time breakdown. `within` is the drill-in
  // breadcrumb (an artifact id, '' = top level) and `crumb` its resolved ancestor chain,
  // fetched alongside. Deliberately ONE axis choice rather than an axis plus a type
  // filter: a filter over a hierarchy double-counts and can't be combined coherently
  // with a drill-in — see Store.costBreakdown.
  // `itype` is the throughput panel's Jira-issuetype filter — canonical keys, comma
  // separated, '' meaning no filter (the same convention `complexity` uses). Ticket-only,
  // and it narrows ONLY that panel: see itypeFilter.ts and the note in dashboard.ts for why
  // the burn and the KPI deliberately ignore it.
  // `by` is the burn panel's split: '' (time axis) | 'team' | 'epic'; `flowBy`
  // is the throughput panel's: '' | 'team'. Team splits are admin-only UI.
  ca: { kind: string; defaultKind: string; userPicked: boolean; kindResolved: boolean; bucket: string; complexity: string; itype: string; by: string; flowBy: string; within: string; crumb: Array<{ id: string; ident: string | null; title: string | null; itype: string | null }>; teamDetail: { id: string; name: string } | null }
  spend: { bucket: string; by: string; filters: Record<string, string[]> } // total-spend detail controls
  // code-churn detail controls. Churn is a trailing metric (docs/plans/
  // churn-rate.md): it ignores the top-level window, so `bucket` is a plain
  // default (not window-derived). `range` is a churn-native cohort span (a day
  // count of matured cohorts, or 'all') — the metric's own time filter, since
  // the 7/14/30/90 selector can't apply. Self/cross is folded into the chart as
  // a secondary breakdown, so there is no view toggle.
  // `windowDays` is the selectable churn horizon T (the removed-within-T cutoff
  // + maturity gate); '' = server default. `hotspots` groups the hotspots panel
  // by file or dir.
  churn: { bucket: string; range: string; windowDays: string; hotspots: 'file' | 'dir' }
  // operational detail: one shared bucket, a per-graph "break down by" choice
  // ('' | 'name' | 'error_category'), and row-level scopes for the error-rate
  // chart (tool names + error categories — ops-specific, not the shared facets).
  ops: { bucket: string; tab: string; by: Record<string, string>; filters: { toolNames: string[]; errorCategories: string[] } }
  // AI Defect Rate detail controls. `lookback` is the metric's W in days — how
  // long after a merge a defect still counts against the origin PR (read-time
  // only; the pipeline stores every defect). `by` is the breakdown dimension
  // ('' = none); bucket follows the same '' = auto rule as every other
  // expansion. `bug` is the open defect drawer's ticket key (mirrored into the
  // URL as ?bug=, the same pattern as ?session=).
  dr: { lookback: number | null; by: string; bucket: string; bug: string | null }
  // time-to-ship (Feature Velocity) detail controls. `bucket` is the trend
  // granularity ('' = window-derived); `by` the breakdown dimension; `complexity`
  // the band filter. DEMO MOCK — see apps/server/src/mock/time-to-ship.ts.
  // `kind` is the v2 sub-tab (ticket | pr); ignored by the v1 panel.
  tts: { bucket: string; by: string; complexity: string[]; kind: string }
  ac: { items: any[]; sel: number } // artifact-search typeahead state
  sessTime: SessTime // sessions-list time window (default 30d)
  // Artifacts tab list controls (PRs/Features table): free-text search + the PR
  // table's column sort. Mirrored into the URL so a filtered/sorted table is a
  // shareable, reload-survivable link. Reset when switching kind (feature ↔ pr).
  art: { q: string; sort: string; dir: string }
  // Benchmarks tab selection: which benchmark / run / task detail is open
  // ('' = the suites list). Mirrored into the URL query so any drill-in is a
  // shareable, reload-survivable link.
  bench: { bench: string; run: string; task: string }
  // Task Replay tab navigation. Three levels, all mirrored into the URL:
  //   L0 (overview)  run='' sel=''        — leaderboard + runs list
  //   L0 (suites)    suite='' run=''      — the suites landing (Tier 0)
  //   L1 (suite)     suite=<id> run=''    — one suite's leaderboard + runs (?suite=)
  //   L2 (run page)  suite=<id> run=<id>  — one sweep's summary + grid  (?run=)
  //   L3 (cell)      run=<id> sel=set     — one cell, key "<task>|<agent>|<model>" (?tr=)
  // ltype/lrepo/lagent are the leaderboard filters (?type=, ?repo=, ?agent=,
  // comma-joined); each is a multi-select where [] = no filter (all).
  tr: { suite: string; run: string; sel: string; ltype: string[]; lrepo: string[]; lagent: string[] }
  // Replay v2 tab: the dataset open ('' = the list) and its routing controls (the model traffic is on now, the
  // margin in Elo, the least savings), all mirrored into the URL.
  rp: { id: string; base: string; margin: string; save: string; task: string; run: string; model: string }
  // Skills tab selection: the open skill's name ('' = the roster). Mirrored
  // into the URL query (?skill=…) like every other drill-in.
  skills: { name: string }
  // Tools tab state: time window, source, kind (mcp|builtin), open entity,
  // and per-tool filter on a detail page.
  toolWin: number | 'all' | 'custom'
  toolFrom: string
  toolTo: string
  toolSource: string
  toolKind: string
  tool: string | null
  toolFilter: string
}

export var state: ClientState = {
  view: 'dashboard', open: null,
  me: null,
  artKind: 'feature', overview: null, home: null, asked: null, filters: {}, facets: [], dist: {}, measures: [],
  metric: null,
  outcomeTypes: [],
  days: 7,
  scope: { team: '', repos: [], user: '' },
  insightsSub: 'highlights',
  hlStale: false,
  filterOpts: null,
  orgUsers: null,
  // bucket '' = auto-derive from the window (bucketForWindow); a manual pick
  // overrides until the window changes. Uniform across every expansion.
  sr: { outcomes: ['session_success'], bucket: '', by: '', filters: {} },
  ca: { kind: 'feature', defaultKind: 'feature', userPicked: false, kindResolved: false, bucket: '', complexity: '', itype: '', by: '', flowBy: '', within: '', crumb: [], teamDetail: null },
  spend: { bucket: '', by: '', filters: {} },
  churn: { bucket: 'week', range: '84', windowDays: '', hotspots: 'file' },
  ops: { bucket: '', tab: 'tools', by: { tool_calls: 'name', error_rate: 'name', skill_usage: 'name' }, filters: { toolNames: [], errorCategories: [] } },
  dr: { lookback: 14, by: '', bucket: '', bug: null },
  tts: { bucket: '', by: 'team', complexity: [], kind: 'ticket' },
  ac: { items: [], sel: -1 },
  sessTime: { preset: 30, from: '', to: '' },
  art: { q: '', sort: 'created', dir: 'desc' },
  bench: { bench: '', run: '', task: '' },
  tr: { suite: '', run: '', sel: '', ltype: [], lrepo: [], lagent: [] },
  rp: { id: '', base: '', margin: '', save: '', task: '', run: '', model: '' },
  skills: { name: '' },
  toolWin: 30, toolFrom: '', toolTo: '', toolSource: '', toolKind: 'mcp', tool: null, toolFilter: ''
};

// The success-rate detail controls persist across reloads: the user's "what
// counts as success" (and the breakdown) becomes their default until they change
// it again. Stored client-side, so the windowed KPI passes the same outcomes to
// the server to stay consistent (see loadKpis). Bucket is intentionally NOT
// persisted — it auto-derives from the window.
var SR_PREFS_KEY = 'tuneloop.sr';
function loadSrPrefs() {
  try {
    var saved = JSON.parse(localStorage.getItem(SR_PREFS_KEY) || 'null');
    if (!saved || typeof saved !== 'object') return;
    if (Array.isArray(saved.outcomes)) state.sr.outcomes = saved.outcomes.filter(function (x) { return typeof x === 'string'; });
    if (typeof saved.by === 'string') state.sr.by = saved.by;
  } catch (e) { /* malformed or unavailable storage → keep defaults */ }
}
export function saveSrPrefs() {
  try { localStorage.setItem(SR_PREFS_KEY, JSON.stringify({ outcomes: state.sr.outcomes, by: state.sr.by })); } catch (e) { /* ignore */ }
}
loadSrPrefs();

export function $(s) { return document.querySelector(s); }
export function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
  });
}
// Minimal, safe Markdown → HTML for assistant message text. Escape-first by
// construction: fenced/inline code is pulled out to placeholders, everything
// else is HTML-escaped (so no raw HTML from the transcript is ever rendered),
// then a small set of block/inline rules are applied. Covers the common cases
// (headings, lists, blockquotes, bold/italic, inline + fenced code, links);
// imperfect on exotic nesting, which is fine for a transcript viewer.
export function renderMd(src) {
  var text = String(src == null ? '' : src);
  var codes = [], inls = [];
  // Private-use sentinel delimiting code placeholders. Never appears in real
  // text, survives esc(), and is stripped by restore(). (A literal char, not a
  // NUL byte, so the file stays text and diffs render.)
  var Z = String.fromCharCode(0xe000);
  // Fenced code blocks first, so their contents are never treated as markdown.
  text = text.replace(/```[ \t]*[\w+-]*\n?([\s\S]*?)```/g, function (_m, code) {
    codes.push('<pre class="md-code"><code>' + esc(code.replace(/\n+$/, '')) + '</code></pre>');
    return Z + 'C' + (codes.length - 1) + Z;
  });
  // Inline code next (single line), so emphasis rules don't touch it.
  text = text.replace(/`([^`\n]+)`/g, function (_m, code) {
    inls.push('<code>' + esc(code) + '</code>');
    return Z + 'I' + (inls.length - 1) + Z;
  });

  var inline = function (s) {
    s = esc(s);
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    s = s.replace(/(^|[^*])\*([^*\s][^*]*?)\*/g, '$1<em>$2</em>');
    s = s.replace(/(^|[^_\w])_([^_\s][^_]*?)_/g, '$1<em>$2</em>');
    s = s.replace(/~~([^~]+)~~/g, '<del>$1</del>');
    // [text](url) — only http/https/relative/anchor hrefs (esc turned & into &amp;).
    s = s.replace(/\[([^\]]+)\]\(((?:https?:\/\/|\/|#)[^\s)]+)\)/g, function (_m, label, href) {
      return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + label + '</a>';
    });
    return s;
  };
  var restore = function (s) {
    return s
      .replace(new RegExp(Z + 'C(\\d+)' + Z, 'g'), function (_m, i) { return codes[Number(i)]; })
      .replace(new RegExp(Z + 'I(\\d+)' + Z, 'g'), function (_m, i) { return inls[Number(i)]; });
  };

  var lines = text.split('\n');
  var out = [], i = 0;
  var flushPara = function (buf) { if (buf.length) out.push('<p>' + buf.map(inline).join('<br>') + '</p>'); };
  while (i < lines.length) {
    var line = lines[i];
    var cb = line.trim().match(new RegExp('^' + Z + 'C(\\d+)' + Z + '$'));
    if (cb) { out.push(codes[Number(cb[1])]); i++; continue; }
    if (!line.trim()) { i++; continue; }
    var h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { var lvl = h[1].length; out.push('<div class="md-h md-h' + lvl + '">' + inline(h[2]) + '</div>'); i++; continue; }
    if (/^\s*>\s?/.test(line)) {
      var q = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { q.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      out.push('<blockquote>' + q.map(inline).join('<br>') + '</blockquote>');
      continue;
    }
    if (/^\s*[-*+]\s+/.test(line)) {
      var ul = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) { ul.push('<li>' + inline(lines[i].replace(/^\s*[-*+]\s+/, '')) + '</li>'); i++; }
      out.push('<ul>' + ul.join('') + '</ul>');
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      var ol = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { ol.push('<li>' + inline(lines[i].replace(/^\s*\d+\.\s+/, '')) + '</li>'); i++; }
      out.push('<ol>' + ol.join('') + '</ol>');
      continue;
    }
    var para = [];
    while (i < lines.length && lines[i].trim() && !new RegExp('^' + Z + 'C\\d+' + Z + '$').test(lines[i].trim()) &&
      !/^(#{1,6})\s+/.test(lines[i]) && !/^\s*>\s?/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i])) { para.push(lines[i]); i++; }
    flushPara(para);
  }
  return restore(out.join(''));
}

export function usd(n) { return '$' + (Number(n) || 0).toFixed(2); }
export function num(n) { return (Number(n) || 0).toLocaleString('en-US'); }
// `credentials: same-origin` sends the httpOnly session cookie on every API
// call (the SPA is served from the same origin as the API). Without it fetch
// would omit the cookie and every guarded endpoint would 401.
export function get(url) { return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.json(); }); }
// Like get, but resolves to { status, body } so callers can branch on the HTTP
// code (e.g. the boot gate distinguishing 401 from a server outage).
export function getStatus(url) {
  return fetch(url, { credentials: 'same-origin' })
    .then(function (r) { return r.json().catch(function () { return {}; }).then(function (b) { return { status: r.status, body: b }; }); });
}
export function post(url, body) {
  return fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then(function (r) { return r.json(); });
}
// Like post, but resolves to { status, body } so auth screens can branch on the
// HTTP code (401/403/409) instead of guessing from the payload shape.
export function postStatus(url, body) {
  return fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    .then(function (r) { return r.json().catch(function () { return {}; }).then(function (b) { return { status: r.status, body: b }; }); });
}
// postStatus with a PUT verb, for the connector settings route — the one route
// that is a partial UPDATE of a single stored object rather than a command, and
// whose patch semantics (absent = leave alone, null = clear) are the verb's.
export function putStatus(url, body) {
  return fetch(url, { method: 'PUT', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    .then(function (r) { return r.json().catch(function () { return {}; }).then(function (b) { return { status: r.status, body: b }; }); });
}
// Fetch-once cache of the org directory (/api/users) for the Sessions user
// filter and the Share picker. Concurrent callers share one in-flight request;
// a failure resolves to [] WITHOUT caching, so the next call retries.
var _orgUsersReq: Promise<NonNullable<ClientState['orgUsers']>> | null = null;
export function loadOrgUsers(): Promise<NonNullable<ClientState['orgUsers']>> {
  if (state.orgUsers) return Promise.resolve(state.orgUsers);
  if (_orgUsersReq) return _orgUsersReq;
  _orgUsersReq = get('/api/users').then(function (r) {
    _orgUsersReq = null;
    state.orgUsers = (r && r.users) || [];
    return state.orgUsers!;
  }).catch(function () { _orgUsersReq = null; return []; });
  return _orgUsersReq;
}

export function dayOf(s) { return s ? String(s).slice(0, 10) : ''; }
// Collapse a home dir to `~/` so screenshots (and any shared UI) don't leak the username.
export function tildeHome(p) { return (p || '').replace(/^(\/Users\/[^/]+|\/home\/[^/]+|\/root)\//, '~/'); }

export function badge(v) {
  var c = v === 'success' ? 'b-success' : v === 'partial' ? 'b-partial' : v === 'failure' ? 'b-failure' : v ? 'b-unknown' : 'b-null';
  return '<span class="badge ' + c + '">' + esc(v || '—') + '</span>';
}

export function grainOfSrc(src) { return src === 'usage' ? 'usage' : src === 'tool-call' ? 'tool_call' : src === 'block' ? 'block' : 'session'; }

export function fmtVal(v, format) {
  if (v == null) return '—';
  if (format === 'usd') return usd(v);
  if (format === 'pct') return Math.round(v * 100) + '%';
  return num(Math.round(v));
}

// Format a composite breakdown key (a ", "-joined value set) for display: a
// multi-value set is truncated to the first few members + "+N", with the full
// set kept for the title/tooltip. Single values and the synthetic "(none)"
// placeholder pass through unchanged. The top-K rollup key "Other" is relabeled
// to "... other values" so it can't be mistaken for a real value named "other"
// (the legend's "Show all" link expands it). Shared by the outcome and
// session-count breakdown charts.
export function comboLabel(key) {
  if (key === '(none)') return { text: key, full: key };
  if (key === 'Other') return { text: '... other values', full: '... other values' };
  var parts = String(key).split(', ');
  if (parts.length <= 1) return { text: key, full: key };
  var MAX = 3, shown = parts.slice(0, MAX).join(', ');
  if (parts.length > MAX) shown += ' +' + (parts.length - MAX);
  return { text: shown, full: parts.join(', ') };
}

export var SR_PALETTE = ['#0f7a55', '#b8860b', '#b4452f', '#3b6ea5', '#7d5ba6', '#1b8a8a', '#a65c2e', '#6b8e23'];

// Complexity bucket → display label. One source of truth shared by the Cost-by-
// Artifact filter buttons and the headline KPI subtext, so they always agree.
export var CX_LABELS: Record<string, string> = {
  trivial: 'Trivial', small: 'Simple', medium: 'Moderate', large: 'Complex', xl: 'Highly Complex', none: 'Not tagged',
};
// Comma-joined bucket keys → "Trivial, Simple" (preserving selection order).
export function cxLabelList(keys: string) {
  return (keys || '').split(',').filter(Boolean).map(function (k) { return CX_LABELS[k] || k; }).join(', ');
}
// A single artifact's complexity → its bucket label. Features carry an ordinal
// (1–5, user-tagged); PRs carry a diff-size churn (line count) bucketed by the
// same ranges as the Cost-by-Artifact filter. Null/untagged → '' (no label).
var CX_ORDINAL = ['', 'trivial', 'small', 'medium', 'large', 'xl'];
export function complexityLabel(kind: string, complexity: number | null | undefined) {
  if (complexity == null) return '';
  if (kind === 'feature') return CX_LABELS[CX_ORDINAL[complexity]] || '';
  var n = Number(complexity);
  var key = n <= 10 ? 'trivial' : n <= 100 ? 'small' : n <= 500 ? 'medium' : n <= 1500 ? 'large' : 'xl';
  return CX_LABELS[key];
}

// Canonical outcome display order: concrete shipped artifacts first, softening
// down to the LLM-judged catch-all. Shared by the success-rate "Count as success"
// picker and the sessions Outcomes column so they read consistently. Types not
// listed sort to the end (preserving their relative order).
export var OUTCOME_ORDER = ['pr_merged', 'pr_created', 'pr_contributed', 'pr_approved', 'pr_changes_requested', 'pr_reviewed', 'commit_pushed', 'file_written', 'session_success'];
export function outcomeRank(type) { var i = OUTCOME_ORDER.indexOf(type); return i < 0 ? OUTCOME_ORDER.length : i; }

// Friendly display for an outcome type in selection UIs (success-rate picker,
// the sessions Outcome filter, active chips). The LLM-judged signal keeps its
// snake_case token (consistent with git-derived outcomes like pr_merged) and
// adds a "(LLM Judged)" cue; every other type shows its raw token unchanged.
export function outcomeLabel(type) {
  return type === 'session_success' ? 'session_success (LLM Judged)' : type;
}

// A delta badge comparing this window's value to the prior window's. mode:
// 'points' for rates (absolute percentage-point change), 'rel' for everything
// else (relative % change). good = which direction is favorable ('up'|'down'):
// the change is green when favorable, red when not. good=null marks a neutral
// metric (spend/sessions) with no better/worse direction — its change shows in
// the brand green (the accent) rather than greyed out, to match the other tiles.
export function kpiDelta(cur, prev, mode, good) {
  if (cur == null || prev == null) return '';
  var diff;
  var text;
  if (mode === 'points') {
    diff = (cur - prev) * 100;
    if (Math.abs(diff) < 0.05) return '<span class="delta flat">±0pp</span>';
    text = (diff > 0 ? '+' : '') + diff.toFixed(1) + 'pp';
  } else {
    if (!prev) return ''; // no baseline to divide by
    diff = ((cur - prev) / Math.abs(prev)) * 100;
    if (Math.abs(diff) < 0.5) return '<span class="delta flat">±0%</span>';
    text = (diff > 0 ? '+' : '') + Math.round(diff) + '%';
  }
  var dir = diff > 0 ? 'up' : 'down';
  var cls = good == null ? 'good' : (dir === good ? 'good' : 'bad');
  var arrow = diff > 0 ? '▲' : '▼';
  return '<span class="delta ' + cls + '">' + arrow + ' ' + esc(text) + '</span>';
}

// ---- window + bucket helpers (shared by every KPI expansion) ----------------

// An appropriate curve granularity for the selected window: fine buckets for
// short spans, coarse for long ones, so charts stay readable.
export function bucketForWindow(days) {
  if (days === 'all') return 'month';
  if (days <= 31) return 'day';
  if (days <= 180) return 'week';
  return 'month';
}

// The effective bucket for an expansion: the user's manual pick ('' = none), or
// the window-derived default. Manual picks are cleared on window change.
export function autoBucket(b) { return b || bucketForWindow(state.days); }

// Query fragment ('&from=…&to=…') pinning an expansion's data to the selected
// window; '' for the all-time window. Appended to an already-non-empty query.
export function windowQs() {
  if (state.days === 'all') return '';
  var span = state.days * 86400000, now = Date.now();
  return '&from=' + encodeURIComponent(new Date(now - span).toISOString()) +
    '&to=' + encodeURIComponent(new Date(now).toISOString());
}

// Query fragment ('&team=…&repo=…') pinning a metric request to the selected
// team/repo scope (AL-100); '' when neither is set (org-global). Sibling of
// windowQs — appended to every metric request so no chart escapes the scope. The
// server resolves/clamps these (a member can't widen past their own team), so the
// client just forwards whatever the selectors hold.
// Whether the viewer may see the admin-only TEAM COMPARISON UI (the
// cost-artifact team splits + panel 3, and total-spend's team breakdown) —
// ONE predicate, because two copies of a role rule is how two pages end up
// disagreeing about who sees what. Requires: an admin (the server 403s
// everyone else), at least two teams (nothing to compare), and NO user or
// team filter — a user belongs to one team and a team filter narrows to one
// team, so either way the "comparison" is one bar and a row of zeros.
export function canCompareTeams() {
  return !!(state.filterOpts && state.filterOpts.role === 'admin' && state.filterOpts.allTeams.length > 1 &&
    !state.scope.user && !state.scope.team);
}

export function scopeQs() {
  // `scope_team`/`scope_repo`, NOT `team`/`repo`, so the top-level scope never
  // collides with a generic facet key. Repo is multi-select → one repeated
  // `scope_repo=` param per selected repo (an OR-set server-side).
  var s = state.scope, qs = '';
  if (s.team) qs += '&scope_team=' + encodeURIComponent(s.team);
  (s.repos || []).forEach(function (r) { if (r) qs += '&scope_repo=' + encodeURIComponent(r); });
  // The user axis (roles milestone). Included here means it also lands in every
  // cache key derived from scopeQs() (costArtifact, caKpiOverride) — switching
  // user can never serve another user's cached tile.
  if (s.user) qs += '&scope_user=' + encodeURIComponent(s.user);
  return qs;
}
